import { useEffect } from 'react'
import { Form, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import * as Yup from 'yup'

import { KeyboardDatePickerInput, dateConversions } from '@dhl/inputs'
import { CheckboxInput, TextFieldInput, SelectInput } from '@dhl/formik'
import { useGetPrismicOneOffPromotionUids } from '@dhl/receiver-dhl-extra'
import {
  usePossibleActionTypes,
  type ActionUpdate,
  type DiscountCodeReward,
  DiscountCodeRewardType,
  ManualRewardType,
  QuizType,
} from '@dhl/receiver-dhl-extra-api'
import { Body1, Box, Button, Card, Grid, MenuItem, H6 } from '@dhl-ui/components'
import { getConfig } from '~/config'
import { QuizInput, defaultQuizFields } from './QuizInput'
import { TermsAndConditionsInput } from './TermsAndConditionsInput'

type Props = {
  id?: string
  title: string
  onCancel?: () => void
}

/**
 * Custom types for a Promotion. `uid` is the value, `title` is the label.`
 *
 * @see #prismicDataToPromotion
 */
type Promotion = {
  uid: string
  title: string
}

const manualRewardTypeText = ManualRewardType.ManualReward
const discountCodeRewardText = DiscountCodeRewardType.DiscountCodeReward
const rewards = [manualRewardTypeText, discountCodeRewardText]

export const createValidationSchema = (t: TFunction) => {
  const requiredFieldError = t('admin.dhlExtraPage.promotionsPage.promotionForm.requiredFieldError')
  return Yup.object()
    .shape({
      title: Yup.string().required(requiredFieldError),
      description: Yup.string().required(requiredFieldError),
      startAt: Yup.date().required(requiredFieldError),
      endAt: Yup.date()
        .required(requiredFieldError)
        .min(Yup.ref('startAt'), t('admin.dhlExtraPage.promotionsPage.promotionForm.dateRangeError')),
      contentId: Yup.string().required(requiredFieldError),
      reward: Yup.object().shape({
        type: Yup.string().required(requiredFieldError),
        discountPercent: Yup.number().when('type', {
          is: (rewardType: ManualRewardType | DiscountCodeRewardType) => rewardType === discountCodeRewardText,
          then: (schema) =>
            schema
              .required(requiredFieldError)
              .integer(t('admin.dhlExtraPage.promotionsPage.promotionForm.integerError'))
              .min(1, t('admin.dhlExtraPage.promotionsPage.promotionForm.discountPercentMinError', { min: 1 }))
              .max(99, t('admin.dhlExtraPage.promotionsPage.promotionForm.discountPercentMaxError', { max: 99 })),
          otherwise: (schema) => schema.nullable(),
        }),
        daysValid: Yup.number().when('type', {
          is: (rewardType: ManualRewardType | DiscountCodeRewardType) => rewardType === discountCodeRewardText,
          then: (schema) =>
            schema
              .required(requiredFieldError)
              .integer(t('admin.dhlExtraPage.promotionsPage.promotionForm.integerError'))
              .min(1, t('admin.dhlExtraPage.promotionsPage.promotionForm.daysValidMinError', { min: 1 })),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
      quiz: Yup.object().when('actionType', {
        is: (actionType: string) => actionType === QuizType.Quiz,
        then: () =>
          Yup.object().shape({
            question: Yup.array().of(
              Yup.object().shape({
                language: Yup.string().required(requiredFieldError),
                value: Yup.string().required(requiredFieldError),
              })
            ),
            answers: Yup.array().of(
              Yup.object().shape({
                key: Yup.string().required(requiredFieldError),
                values: Yup.array().of(
                  Yup.object().shape({
                    language: Yup.string().required(requiredFieldError),
                    value: Yup.string().required(requiredFieldError),
                  })
                ),
              })
            ),
          }),
        otherwise: (schema) => schema.nullable(),
      }),
      termsAndConditions: Yup.object().shape({
        mustAcknowledge: Yup.boolean(),
        links: Yup.array().of(
          Yup.object().shape({
            language: Yup.string().required(requiredFieldError),
            link: Yup.string().required(requiredFieldError),
          })
        ),
      }),
    })
    .defined()
}

/**
 * The `useGetPrismicOneOffPromotionUids()` hook is supposed to return a `string[]` of uid's according to its
 * interface, but it does not. Since at the moment it returns a Prismic document, so the code below maps to a
 * `Promotion[]` (a union of uid+title) in the latter case. The guard around the code is to make sure it functions in
 * both cases.
 * The *actual* fix for this lies with Team Receiver.
 *
 * @see https://dhlecommercebnl.slack.com/archives/CFCB6FGBA/p1776760559066929
 */

const prismicDataToPromotion = (promotions: string[] | Promotion[]): Promotion[] =>
  promotions.every((item) => typeof item === 'string')
    ? promotions.map((uid) => ({ uid, title: uid }))
    : (promotions as unknown as Promotion[]).map(({ uid, title }) => ({ uid, title }))

export const PromotionForm = ({ title, onCancel, id }: Props) => {
  const { receiverPrismicEndpoint } = getConfig()
  const { t } = useTranslation()
  const { data: promotionData, isLoading } = useGetPrismicOneOffPromotionUids({
    prismicEndpoint: receiverPrismicEndpoint,
  })
  const promotions = !isLoading && promotionData ? prismicDataToPromotion(promotionData) : []
  const { possibleActionTypes } = usePossibleActionTypes({ promotionId: id })
  const {
    initialValues: { quiz: initialQuizValue },
    values: { reward, actionType, quiz },
    setFieldValue,
  } = useFormikContext<ActionUpdate>()

  useEffect(() => {
    const { type } = reward
    const { daysValid, discountPercent } = reward as DiscountCodeReward

    if (type === discountCodeRewardText && daysValid === undefined && discountPercent === undefined) {
      setFieldValue('reward', { type: discountCodeRewardText, discountPercent: '', daysValid: '' })
      return
    }

    if (type === manualRewardTypeText && (daysValid !== undefined || discountPercent !== undefined)) {
      setFieldValue('reward', { type: manualRewardTypeText })
    }
  }, [reward])

  useEffect(() => {
    if (actionType === QuizType.Quiz) {
      setFieldValue('allowSubscribe', true)
      setFieldValue('quiz', initialQuizValue ?? defaultQuizFields)
    } else {
      setFieldValue('allowSubscribe', false)
      setFieldValue('quiz', undefined)
    }
  }, [actionType])

  const shouldShowAllowSubscribeField = actionType !== QuizType.Quiz
  const shouldShowDiscountRewardFields =
    reward?.type === discountCodeRewardText && reward.daysValid !== undefined && reward.discountPercent !== undefined
  const shouldShowQuizFields = actionType === QuizType.Quiz && quiz?.question && quiz.answers

  return (
    <Card>
      <Card.Header title={title} />
      <Card.Content>
        <Box pb={2}>
          <H6>{t('admin.dhlExtraPage.promotionsPage.promotionForm.subTitle')}</H6>
        </Box>

        <Form data-testid="dhlExtra-promotion-form">
          <Box>
            <Grid container spacing={2} direction="column">
              <Grid>
                <TextFieldInput
                  label={t('admin.dhlExtraPage.promotionsPage.promotionForm.title')}
                  name="title"
                  type="text"
                  fullWidth
                />
              </Grid>
              <Grid>
                <TextFieldInput
                  fullWidth
                  multiline
                  label={t('admin.dhlExtraPage.promotionsPage.promotionForm.description')}
                  name="description"
                  rows={5}
                  type="text"
                />
              </Grid>
              <Grid>
                <SelectInput name="actionType" label={t('admin.dhlExtraPage.promotionsPage.promotionForm.actionType')}>
                  {possibleActionTypes.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>{`${label}`}</MenuItem>
                  ))}
                </SelectInput>
              </Grid>
              <Grid container wrap="nowrap">
                <Grid>
                  <KeyboardDatePickerInput
                    conversions={dateConversions}
                    label={t('admin.dhlExtraPage.promotionsPage.promotionForm.startAt')}
                    name="startAt"
                  />
                </Grid>
                <Grid>
                  <KeyboardDatePickerInput
                    conversions={dateConversions}
                    label={t('admin.dhlExtraPage.promotionsPage.promotionForm.endAt')}
                    name="endAt"
                  />
                </Grid>
              </Grid>
              <Grid>
                <SelectInput
                  name="contentId"
                  label={t('admin.dhlExtraPage.promotionsPage.promotionForm.contentId')}
                  fullWidth
                >
                  {promotions?.map(({ uid, title }, index) => (
                    // key collision in `uid`, added `index`
                    <MenuItem key={`${uid}-${index}`} value={uid}>
                      {title}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Grid>
              {shouldShowAllowSubscribeField && (
                <Grid>
                  <CheckboxInput
                    name="allowSubscribe"
                    label={t('admin.dhlExtraPage.promotionsPage.promotionForm.allowSubscribe')}
                  />
                </Grid>
              )}
              {shouldShowQuizFields && <QuizInput name="quiz" />}
              <Grid>
                <SelectInput
                  name="reward.type"
                  label={t('admin.dhlExtraPage.promotionsPage.promotionForm.reward.label')}
                  fullWidth
                >
                  {rewards.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`admin.dhlExtraPage.promotionsPage.promotionForm.reward.${value}`)}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Grid>
              {shouldShowDiscountRewardFields && (
                <>
                  <Grid size={{ xs: 6 }}>
                    <TextFieldInput
                      fullWidth
                      label={t('admin.dhlExtraPage.promotionsPage.promotionForm.reward.discountPercent')}
                      name="reward.discountPercent"
                      type="number"
                      slotProps={{ input: { endAdornment: '%' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextFieldInput
                      fullWidth
                      label={t('admin.dhlExtraPage.promotionsPage.promotionForm.reward.daysValid')}
                      name="reward.daysValid"
                      type="number"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Body1>{t('admin.dhlExtraPage.promotionsPage.promotionForm.reward.days')}</Body1>
                          ),
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
              <TermsAndConditionsInput name="termsAndConditions" />
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
              <Button data-testid="click-cancel" size="medium" color="primary" onClick={onCancel}>
                {t('admin.dhlExtraPage.promotionsPage.promotionForm.buttonCancel')}
              </Button>
              <Button size="medium" color="primary" type="submit" variant="contained">
                {t('admin.dhlExtraPage.promotionsPage.promotionForm.buttonSave')}
              </Button>
            </Box>
          </Box>
        </Form>
      </Card.Content>
    </Card>
  )
}
